import { ContentSubpageParams } from "@plugins/content-subpage/composables/content-subpage.ts";

export interface AcpHtml5GenerationParams extends ContentSubpageParams {
  richTextContext: object;
}

export interface AcpHtml5IterationParams extends ContentSubpageParams {
  html5List?: Html5ListItem[];
  requestId?: string;
  richTextContext?: object;
}

export interface ButtonState {
  error: boolean;
  errorMessage: string;
  loading: boolean;
  completed: boolean;
  iconOnly: boolean;
  tipPosition: string;
  tipAlignment: string;
}

export interface GenerateButtonState extends ButtonState {
  kind?: string;
  size?: string;
  iconName: string;
}

export interface RefineButtonState extends ButtonState {
  targetDraftId: string | null;
}

export interface AcpStatusResponse {
  status: string;
  errorCode?: string;
}

export interface AcpResponse {
  id: string;
}

export interface AAHtml5RecommendationRequestModel {
  rt_instruction: string;
  knowledgeBaseIds?: number[];
  mediaObjectUuid?: string;
  previousHtml5DraftRequestId?: string;
}

export interface AcpImageRequest {
  acpImageType: AcpImageEntryType;
  title: string;
  description: string;
  rt_instruction: string;
  size: AcpImageSize;
}

export interface AcpGeneratedImageFile extends File {
  fileUuid: string;
  acpRequestId: string;
}

export interface AcpMo {
  uuid: string;
  fileType: string;
  fileName: string;
  type: string; // "mo" or "ugc"
}

export interface DraftState {
  expanded: boolean;
  selectedRecipeId: number | null;
  instructions: string;
  hasUpdatedInstructionValue: boolean;
}

export interface Html5ListItem {
  id: string;
  fileUrl?: string;
  version?: string;
  type: string;
}

export const AcpImageEntryType = {
  GAME_STORY: "GAME_STORY",
  COLLECTIBLE: "COLLECTIBLE",
  DEFAULT: "DEFAULT",
  GAMIFICATION: "GAMIFICATION",
  FEATURED_IMAGE: "FEATURED_IMAGE",
  DIGITAL_BADGE: "DIGITAL_BADGE",
  CONTENT_MAP_THUMBNAIL: "CONTENT_MAP_THUMBNAIL",
} as const;
export type AcpImageEntryType = (typeof AcpImageEntryType)[keyof typeof AcpImageEntryType];

export const AcpImageEntryAction = {
  EDIT: "EDIT",
  CREATE: "CREATE",
  NONE: "NONE",
} as const;
export type AcpImageEntryAction = (typeof AcpImageEntryAction)[keyof typeof AcpImageEntryAction];

export const AcpImageSize = {
  STANDARD_3_2: "1008x672",
  BACKGROUND_2_1: "1280x640",
  WIDE_21_9: "1344x576",
  SQUARE_1_1: "816x816",
} as const;
export type AcpImageSize = (typeof AcpImageSize)[keyof typeof AcpImageSize];

export enum KnowledgeBaseContentType {
  TEXT = "TEXT",
  MEDIA = "MEDIA",
  GOOGLE = "GOOGLE",
}

export enum MediaObjectType {
  AUDIO = "AUDIO",
  HTML5 = "HTML5",
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
  EXCEL = "EXCEL",
  POWERPOINT = "POWERPOINT",
  WORD = "WORD",
  PDF = "PDF",
  ZIP = "ZIP",
  OTHERS = "OTHERS",
}

/**
 * This is the type returned by the BE for `AAKnowledgeBaseModel.mimeType`.
 */
export enum MediaObjectMimeType {
  PDF = "application/pdf",
  JPEG = "image/jpeg",
  GIF = "image/gif",
  PNG = "image/png",
  JPG = "image/jpg",
  DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  TXT = "text/plain",
}

export interface KnowledgeBase {
  id: number;
  contentType: KnowledgeBaseContentType;
  content?: string;
  contentId?: string;
  mediaTitle: string;
  mediaType: MediaObjectType;
  indexNo: number;
  // NEW — enriched by BE for FE validation
  fileSize?: number; // bytes; null means size unknown (e.g. Google still converting)
  mimeType?: MediaObjectMimeType;
}

export interface AcpGamificationRequest {
  rt_instruction: string;
  lessonId: number;
  numOfGameStories: number;
  numOfAchievements: number;
  numOfGameAvatars: number;
  numOfDigitalBadges: number;
}

export enum AcpConditionType {
  SECTION_START = "SECTION_START",
  ACTIVITY_START = "ACTIVITY_START",
  SECTION_COMPLETION = "SECTION_COMPLETION",
  ACTIVITY_COMPLETION = "ACTIVITY_COMPLETION",
  MODULE_START = "MODULE_START",
  QUIZ_SCORE = "QUIZ_SCORE",
}

export interface AcpGeneratedModule {
  lessonActivityList: object[];
  title: string;
  description: string;
  showCover: boolean;
  showSectionCover: boolean;
}
